import { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, NavLink } from "react-router-dom";
import axios from "axios";
import useSWR from "swr";
import {
  ArrowRight,
  CalendarDays,
  Check,
  Clock,
  Instagram,
  Mail,
  Phone,
  Plus,
  ShieldCheck,
  UserRound,
  Users,
  X,
} from "lucide-react";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const fetcher = (url) => axios.get(url).then((response) => response.data);
const DATE_OPTION_DAYS = 21;
const CALENDAR_GRID_DAYS = 42;
const BLACKOUT_YEAR = 2026;
const JUNE_MONTH_INDEX = 5;
const JULY_MONTH_INDEX = 6;
const OCTOBER_MONTH_INDEX = 9;
const NOVEMBER_MONTH_INDEX = 10;
const JUNE_BLACKOUT_START_DAY = 25;
const JUNE_BLACKOUT_END_DAY = 29;
const JULY_BLACKOUT_DAY = 2;
const OCTOBER_BLACKOUT_START_DAY = 26;
const OCTOBER_BLACKOUT_END_DAY = 31;
const NOVEMBER_BLACKOUT_DAY = 1;
const BOOKING_START_TIMES = ["07:00", "08:00", "09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00", "18:00", "19:00"];
const ICON_SIZE_SM = 17;
const INLINE_ICON_SIZE = 13;
const TRAINER_VISUAL_MIN_HEIGHT_CLASS = "min-h-[420px]";
const APP_ASSETS = {
  logo: "/assets/msp-logo.png",
  home: "/assets/home-panel.jpg",
  about: "/assets/about-panel.jpg",
  book: "/assets/book-panel.jpg",
  contact: "/assets/contact-panel.jpg",
};

const navItems = [
  { to: "/", label: "Home", testId: "nav-home-link" },
  { to: "/about", label: "About Me", testId: "nav-about-link" },
  { to: "/book", label: "Book", testId: "nav-book-link" },
  { to: "/contact", label: "Contact", testId: "nav-contact-link" },
];

const focusOptions = [
  "Pad work assessment",
  "Footwork + defensive rhythm",
  "Power combinations",
  "Counter-punching mechanics",
  "Conditioning + bag rounds",
  "Beginner fundamentals",
];

const dateOptions = Array.from({ length: DATE_OPTION_DAYS }, (_, index) => {
  const date = new Date();
  date.setDate(date.getDate() + index + 1);
  const value = date.toISOString().slice(0, 10);
  return { value, label: formatDate(value) };
});

const timeOptions = [
  "06:00", "06:30", "07:00", "07:30", "08:00", "08:30", "09:00", "09:30",
  "10:00", "10:30", "11:00", "11:30", "12:00", "12:30", "13:00", "13:30",
  "14:00", "14:30", "15:00", "15:30", "16:00", "16:30", "17:00", "17:30",
  "18:00", "18:30", "19:00", "19:30", "20:00",
];

const emptyBooking = {
  client_name: "",
  email: "",
  phone: "",
  experience_level: "Beginner",
  party_size: 1,
  notes: "",
  agreement_accepted: false,
};

const agreementPoints = [
  ["Purpose of Training", "I understand this private training program is designed to help me improve physical conditioning, boxing skills, strength, endurance, confidence, and overall athletic performance in a professional and supportive environment."],
  ["Client Eligibility & Health Disclosure", "I confirm I am physically capable of participating and that I have disclosed all relevant medical information before training."],
  ["Assumption of Risk", "I understand and assume all risks associated with boxing, athletic training, conditioning, and physical activity."],
  ["Waiver and Release of Liability", "I release the trainer from liability except in cases of gross negligence."],
  ["Training Structure", "I understand sessions are private, by appointment only, and paid in advance."],
  ["Payment and Cancellation", "I understand 24-hour notice is required for cancellations and that no-shows are charged as completed sessions."],
  ["Media Usage", "I consent to media usage unless I opt out in writing before the session."],
];

const whyTrainItems = [
  "Undefeated Professional Boxer",
  "Experienced Training & Competed Internationally",
  "Professional-Level Technique & Fight Preparation",
  "Personalized 1-on-1 Coaching",
  "Boxing, Conditioning & Performance Training",
  "Proven Methods Used at the Highest Level",
];

const emptySlot = {
  date: dateOptions[0]?.value || "",
  start_time: "07:00",
  end_time: "08:00",
  focus: focusOptions[0],
  status: "open",
};

function isBlackoutDateValue(dateValue) {
  const date = new Date(`${dateValue}T12:00:00`);
  const juneBlackout = date.getFullYear() === BLACKOUT_YEAR && date.getMonth() === JUNE_MONTH_INDEX && date.getDate() >= JUNE_BLACKOUT_START_DAY && date.getDate() <= JUNE_BLACKOUT_END_DAY;
  const julyBlackout = date.getFullYear() === BLACKOUT_YEAR && date.getMonth() === JULY_MONTH_INDEX && date.getDate() === JULY_BLACKOUT_DAY;
  const octoberBlackout = date.getFullYear() === BLACKOUT_YEAR && date.getMonth() === OCTOBER_MONTH_INDEX && date.getDate() >= OCTOBER_BLACKOUT_START_DAY && date.getDate() <= OCTOBER_BLACKOUT_END_DAY;
  const novemberBlackout = date.getFullYear() === BLACKOUT_YEAR && date.getMonth() === NOVEMBER_MONTH_INDEX && date.getDate() === NOVEMBER_BLACKOUT_DAY;
  return juneBlackout || julyBlackout || octoberBlackout || novemberBlackout;
}

function getEasternTodayValue() {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

function getEasternFirstBookableDateValue() {
  const today = new Date(`${getEasternTodayValue()}T12:00:00`);
  today.setDate(today.getDate() + 1);
  return today.toISOString().slice(0, 10);
}

function testIdSlug(value) {
  return value.toLowerCase().replaceAll(" & ", " and ").replaceAll(" ", "-");
}

function getClientAvailabilityForDate(dateValue, sessionType) {
  if (dateValue < getEasternFirstBookableDateValue()) return [];
  if (isBlackoutDateValue(dateValue)) return [];
  const date = new Date(`${dateValue}T12:00:00`);
  if (date.getDay() === 0) return [];
  return BOOKING_START_TIMES.map((start) => ({
    id: `fallback-${sessionType}-${dateValue}-${start}`,
    date: dateValue,
    start_time: start,
    end_time: new Date(`1970-01-01T${start}:00`).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "UTC" }) === start ? (() => {
      const parsed = new Date(`1970-01-01T${start}:00`);
      parsed.setHours(parsed.getHours() + 1);
      return parsed.toTimeString().slice(0, 5);
    })() : start,
    focus: sessionType === "group" ? "Group class" : "Private boxing session",
    session_type: sessionType,
    capacity: sessionType === "group" ? 6 : 1,
    booked_count: 0,
    status: "open",
    fallback: true,
  }));
}

function useStudioData() {
  const trainerQuery = useSWR(`${API}/trainer`, fetcher);
  const sessionsQuery = useSWR(`${API}/sessions`, fetcher);
  const bookingsQuery = useSWR(`${API}/bookings`, fetcher);
  const clientsQuery = useSWR(`${API}/clients`, fetcher);
  const statsQuery = useSWR(`${API}/stats`, fetcher);

  const refreshAll = async () => Promise.all([
    trainerQuery.mutate(),
    sessionsQuery.mutate(),
    bookingsQuery.mutate(),
    clientsQuery.mutate(),
    statsQuery.mutate(),
  ]);

  const trainer = trainerQuery.data || null;
  const sessions = sessionsQuery.data || [];
  const bookings = bookingsQuery.data || [];
  const clients = clientsQuery.data || [];
  const stats = statsQuery.data || null;
  const loading = trainerQuery.isLoading && !trainer;

  return { trainer, sessions, bookings, clients, stats, loading, refreshAll };
}

function formatDate(dateString) {
  return new Date(`${dateString}T12:00:00`).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function formatTime(timeString) {
  return new Date(`1970-01-01T${timeString}:00`).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function monthKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function buildCalendarDays(activeMonth) {
  const first = new Date(activeMonth.getFullYear(), activeMonth.getMonth(), 1);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  return Array.from({ length: CALENDAR_GRID_DAYS }, (_, index) => {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    return day;
  });
}

function isoDate(date) {
  return date.toISOString().slice(0, 10);
}

function groupSessionsByDate(sessions) {
  return sessions.reduce((grouped, currentSession) => {
    grouped[currentSession.date] = grouped[currentSession.date] || [];
    grouped[currentSession.date].push(currentSession);
    return grouped;
  }, {});
}

function dedupeSessions(sessions) {
  const seen = new Set();
  return sessions.filter((session) => {
    const key = [session.date, session.start_time, session.end_time, session.session_type || "private"].join("|");
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function buildSessionMap(sessions) {
  return Object.fromEntries(sessions.map((currentSession) => [currentSession.id, currentSession]));
}

function getEmptySessionMessage(bookingType) {
  if (bookingType === "group") {
    return "No open group classes right now.";
  }
  return "No open private sessions right now.";
}

function statusClass(status) {
  if (status === "approved") return "status status-approved";
  if (status === "rejected") return "status status-rejected";
  if (status === "held") return "status status-held";
  if (status === "closed") return "status status-rejected";
  return "status status-pending";
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="app-shell">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#050505]/80 backdrop-blur-xl" data-testid="top-navigation">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-5 py-4 lg:px-8">
          <NavLink to="/" className="flex items-center gap-3" data-testid="brand-home-link">
            <span className="brand-logo-image-wrap" data-testid="brand-icon">
              <img src={APP_ASSETS.logo} alt="MSP Matteo Stallion Papa logo" data-testid="brand-logo-image" />
            </span>
            <span>
              <span className="block font-display text-2xl leading-none tracking-normal" data-testid="brand-name">STAY SHARP</span>
              <span className="block text-xs uppercase tracking-[0.18em] text-[#9CA3AF]" data-testid="brand-subtitle">PRIVATE BOXING SESSION</span>
            </span>
          </NavLink>
          <nav className="flex items-center gap-1" data-testid="primary-navigation">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testId}
                className={({ isActive }) =>
                  `nav-link ${isActive ? "nav-link-active" : ""}`
                }
                end={item.to === "/"}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </header>
      {children}
      <footer className="border-t border-[#27272A] bg-[#050505]" data-testid="site-footer">
        <div className="mx-auto grid max-w-7xl gap-8 px-5 py-10 md:grid-cols-[1fr_auto] lg:px-8">
          <div data-testid="footer-brand-block">
            <p className="font-display text-3xl tracking-normal" data-testid="footer-brand-name">STAY SHARP</p>
            <p className="mt-2 max-w-xl text-sm text-[#A1A1AA]" data-testid="footer-description">PRIVATE BOXING SESSION with me. Book your session online and receive instant confirmation.</p>
          </div>
        </div>
      </footer>
      <Toaster richColors theme="dark" position="bottom-right" />
    </div>
  );
}

function HomePage({ data }) {
  const { trainer } = data;

  return (
    <main data-testid="home-page">
      <section className="website-hero" data-testid="website-hero-section">
        <div className="website-hero-overlay" />
        <div className="relative mx-auto grid min-h-[calc(92vh-78px)] max-w-7xl content-center gap-10 px-5 py-16 lg:grid-cols-[1fr_0.75fr] lg:px-8">
          <div className="animate-in" data-testid="home-hero-copy">
            <span className="eyebrow" data-testid="home-hero-eyebrow">Premium private boxing coaching</span>
            <h1 className="mt-5 max-w-4xl font-display text-5xl tracking-normal text-white sm:text-6xl" data-testid="home-hero-title">STAY SHARP WITH ME.</h1>
            <div className="home-proof-block" data-testid="home-proof-block">
              <h2 className="font-display text-3xl tracking-normal" data-testid="home-proof-title">Why train with me:</h2>
              <ul className="home-proof-list" data-testid="home-proof-list">
                {whyTrainItems.map((item) => (
                  <li key={item} data-testid={`home-proof-item-${item.toLowerCase().replaceAll(" ", "-").replaceAll("&", "and")}`}>{item}</li>
                ))}
              </ul>
              <div className="home-philosophy" data-testid="home-philosophy-block">
                <span className="eyebrow" data-testid="home-philosophy-eyebrow">My Philosophy</span>
                <p data-testid="home-philosophy-quote">“Champions are built long before fight night. Success comes from discipline, consistency, and the willingness to do what others won’t.”</p>
              </div>
            </div>
            <div className="mt-8 flex flex-wrap gap-3" data-testid="home-hero-actions">
              <NavLink to="/book" className="primary-button" data-testid="home-book-session-button">Book a session <ArrowRight size={ICON_SIZE_SM} className="ml-2" /></NavLink>
              <NavLink to="/about" className="secondary-button" data-testid="home-about-me-button">About Me</NavLink>
            </div>
          </div>
          <aside className="brand-statement-panel photo-panel home-photo-panel animate-in delay-2" data-testid="home-brand-panel">
            <img src={APP_ASSETS.home} alt="Stay Sharp boxing moment" data-testid="home-brand-photo" />
            <div className="photo-panel-overlay" data-testid="home-brand-photo-overlay" />
            <span className="eyebrow" data-testid="home-brand-eyebrow">Private boxing session</span>
            <p className="font-display text-5xl tracking-normal" data-testid="home-brand-copy">PUSH YOUR LIMITS. SHARPEN YOUR MIND. TRANSFORM YOUR BODY.</p>
          </aside>
        </div>
      </section>

      <HomeFeaturesSection />
    </main>
  );
}

function HomeFeaturesSection() {
  const features = [
    ["BOXING TECHNIQUE", "Clean fundamentals, controlled combinations, footwork, defense, and ring IQ."],
    ["STRENGTH & CONDITIONING", "Fight-style rounds, athletic conditioning, and strength work adapted to your level."],
  ];
  return (
    <section className="mx-auto grid max-w-7xl gap-6 px-5 py-16 md:grid-cols-3 lg:px-8" data-testid="home-feature-grid">
      {features.map(([title, copy]) => {
        const slug = testIdSlug(title);
        return (
          <article className="premium-card" data-testid={`home-feature-${slug}`} key={title}>
            <h2 className="font-display text-3xl tracking-normal" data-testid={`home-feature-${slug}-title`}>{title}</h2>
            <p className="mt-3 text-sm leading-7 text-[#A1A1AA]" data-testid={`home-feature-${slug}-copy`}>{copy}</p>
          </article>
        );
      })}
    </section>
  );
}

function AboutPage() {
  return (
    <main className="mx-auto max-w-7xl px-5 py-14 lg:px-8" data-testid="about-page">
      <section className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr]" data-testid="about-matteo-section">
        <div className="brand-statement-panel photo-panel" data-testid="about-brand-statement-panel">
          <img src={APP_ASSETS.about} alt="Matteo Papa private boxing training" data-testid="about-brand-photo" />
          <div className="photo-panel-overlay" data-testid="about-brand-photo-overlay" />
          <span className="eyebrow" data-testid="about-brand-eyebrow">Stay Sharp Method</span>
          <p className="font-display text-5xl tracking-normal" data-testid="about-brand-statement">Discipline. Perseverance.</p>
        </div>
        <div className="content-center py-6" data-testid="about-matteo-copy">
          <span className="eyebrow" data-testid="about-eyebrow">About Me</span>
          <h1 className="mt-4 font-display text-5xl tracking-normal sm:text-6xl" data-testid="about-title">Private coaching with a pro fighter’s eye.</h1>
          <p className="mt-5 text-base leading-8 text-[#D4D4D8]" data-testid="about-description">Matteo “Stallion” Papa is an undefeated professional boxer signed with Mayweather Promotions, who has trained and competed at the highest levels of the sport. His journey has taken him from Italy to some of the world’s most demanding boxing environments, where he has developed the discipline, technique, and mindset required to succeed inside the ring.</p>
          <p className="mt-4 text-base leading-8 text-[#D4D4D8]" data-testid="about-description-secondary">Training under elite coaches and competing professionally has given him a deep understanding of what separates good athletes from great ones. Every lesson he has learned through years of sacrifice, preparation, and competition is now built into his coaching system.</p>
          <p className="mt-4 text-base leading-8 text-[#D4D4D8]" data-testid="about-description-third">His private sessions are designed for individuals who want more than just a workout. Whether someone is a beginner looking to learn proper boxing fundamentals or an experienced athlete aiming to sharpen their skills, each session is tailored to their goals, abilities, and level of commitment.</p>
          <p className="mt-4 text-base leading-8 text-[#D4D4D8]" data-testid="about-description-fourth">Clients won’t find cookie-cutter training here. Every session focuses on technical development, footwork, conditioning, ring IQ, and the mental discipline that boxing demands. His goal is simple: help clients move better, think faster, train smarter, and build confidence that extends beyond the gym.</p>
        </div>
      </section>
    </main>
  );
}

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <div className="metric-card" data-testid={testId}>
      <Icon size={18} className="text-[#81D8D0]" aria-hidden="true" />
      <span className="text-xs uppercase tracking-[0.16em] text-[#9CA3AF]" data-testid={`${testId}-label`}>{label}</span>
      <strong className="font-display text-4xl font-normal tracking-normal" data-testid={`${testId}-value`}>{value}</strong>
    </div>
  );
}

function SessionCard({ session, selected, onSelect }) {
  const isOpen = session.status === "open";
  const remaining = session.capacity - (session.booked_count || 0);
  return (
    <button
      type="button"
      data-testid={`session-slot-${session.id}`}
      onClick={() => isOpen && onSelect(session)}
      disabled={!isOpen}
      className={`time-option ${selected ? "time-option-selected" : ""} ${!isOpen ? "time-option-disabled" : ""}`}
    >
      {selected && <span className="text-xs uppercase tracking-[0.14em] text-[#81D8D0]" data-testid={`session-selected-label-${session.id}`}>Selected</span>}
      <span className="font-display text-2xl tracking-normal" data-testid={`session-time-${session.id}`}>{formatTime(session.start_time)}</span>
      <span className="text-xs uppercase tracking-[0.14em] text-[#9CA3AF]" data-testid={`session-end-time-${session.id}`}>Until {formatTime(session.end_time)}</span>
      {session.session_type === "group" && <span className="text-xs uppercase tracking-[0.14em] text-[#9CA3AF]" data-testid={`session-spots-${session.id}`}>{remaining}/{session.capacity} spots</span>}
    </button>
  );
}

function BookingCalendar({ sessions, selectedDate, onSelectDate, activeMonth, setActiveMonth, sessionType }) {
  const days = buildCalendarDays(activeMonth);
  const sessionsByDate = groupSessionsByDate(sessions);

  const shiftMonth = (amount) => {
    const next = new Date(activeMonth);
    next.setMonth(activeMonth.getMonth() + amount);
    setActiveMonth(next);
  };

  return (
    <div className="calendar-panel" data-testid={`${sessionType}-calendar-panel`}>
      <div className="calendar-header" data-testid={`${sessionType}-calendar-header`}>
        <button type="button" className="icon-button" data-testid={`${sessionType}-calendar-prev-month`} onClick={() => shiftMonth(-1)}>‹</button>
        <h3 className="font-display text-3xl tracking-normal" data-testid={`${sessionType}-calendar-month`}>{activeMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" })}</h3>
        <button type="button" className="icon-button" data-testid={`${sessionType}-calendar-next-month`} onClick={() => shiftMonth(1)}>›</button>
      </div>
      <div className="calendar-weekdays" data-testid={`${sessionType}-calendar-weekdays`}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => <span key={day} data-testid={`${sessionType}-weekday-${day.toLowerCase()}`}>{day}</span>)}
      </div>
      <div className="calendar-grid" data-testid={`${sessionType}-calendar-grid`}>
        {days.map((day) => {
          const date = isoDate(day);
          const available = sessionsByDate[date]?.some((session) => session.status === "open");
          const outside = day.getMonth() !== activeMonth.getMonth();
          return (
            <button
              type="button"
              key={date}
              data-testid={`${sessionType}-calendar-date-${date}`}
              className={`calendar-day ${selectedDate === date ? "calendar-day-selected" : ""} ${available ? "calendar-day-available" : ""} ${outside ? "calendar-day-outside" : ""}`}
              onClick={() => available && onSelectDate(date)}
              disabled={!available}
            >
              <span data-testid={`${sessionType}-calendar-date-number-${date}`}>{day.getDate()}</span>
              {available && <small data-testid={`${sessionType}-calendar-date-available-${date}`}>Available</small>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function BookingTypeTabs({ bookingType, setBookingType }) {
  return (
    <div className="booking-tabs" data-testid="booking-type-tabs">
      <button type="button" className={bookingType === "private" ? "booking-tab active" : "booking-tab"} data-testid="private-session-tab" onClick={() => setBookingType("private")}>Private Session</button>
      <button type="button" className={bookingType === "group" ? "booking-tab active" : "booking-tab"} data-testid="group-class-tab" onClick={() => setBookingType("group")}>Group Class</button>
    </div>
  );
}

function BookingFields({ bookingType, form, handleChange }) {
  return (
    <div className="form-grid" data-testid="booking-form-grid">
      <label data-testid="client-name-label">Name<input data-testid="client-name-input" name="client_name" value={form.client_name} onChange={handleChange} required placeholder="Your full name" /></label>
      <label data-testid="client-email-label">Email<input data-testid="client-email-input" name="email" type="email" value={form.email} onChange={handleChange} required placeholder="you@email.com" /></label>
      <label data-testid="client-phone-label">Phone<input data-testid="client-phone-input" name="phone" value={form.phone} onChange={handleChange} required placeholder="Best contact number" /></label>
      {bookingType === "private" && <label data-testid="experience-level-label">Experience<select data-testid="experience-level-select" name="experience_level" value={form.experience_level} onChange={handleChange}>
        <option>Beginner</option><option>Intermediate</option><option>Advanced</option><option>Competitive</option>
      </select></label>}
      {bookingType === "group" && <label data-testid="party-size-label">People<select data-testid="party-size-select" name="party_size" value={form.party_size} onChange={handleChange}>{[1, 2, 3, 4, 5, 6].map((count) => <option key={count} value={count}>{count}</option>)}</select></label>}
      {bookingType === "private" && <label className="md:col-span-2" data-testid="client-notes-label">Notes<textarea data-testid="client-notes-textarea" name="notes" value={form.notes} onChange={handleChange} placeholder="Injuries, schedule limits, or fight prep details" /></label>}
    </div>
  );
}

function AgreementSection({ form, handleChange }) {
  return (
    <section className="agreement-box" data-testid="booking-agreement-section">
      <div className="section-heading mb-4" data-testid="booking-agreement-heading">
        <span className="eyebrow" data-testid="booking-agreement-eyebrow">Client agreement</span>
        <h3 className="font-display text-3xl tracking-normal" data-testid="booking-agreement-title">Before you confirm a session</h3>
      </div>
      <div className="agreement-list" data-testid="booking-agreement-list">
        {agreementPoints.map(([title, copy], index) => (
          <article className="agreement-item" data-testid={`agreement-point-${index + 1}`} key={title}>
            <strong data-testid={`agreement-point-${index + 1}-title`}>{index + 1}. {title}</strong>
            <p data-testid={`agreement-point-${index + 1}-copy`}>{copy}</p>
          </article>
        ))}
      </div>
      <label className="agreement-checkbox" data-testid="agreement-acceptance-label">
        <input
          data-testid="agreement-acceptance-checkbox"
          type="checkbox"
          name="agreement_accepted"
          checked={form.agreement_accepted}
          onChange={handleChange}
          required
        />
        <span data-testid="agreement-acceptance-text">I have read and accept every point of this agreement.</span>
      </label>
    </section>
  );
}

function useBookingFlow(data) {
  const { sessions, refreshAll } = data;
  const [bookingType, setBookingType] = useState("private");
  const [selectedSessionIds, setSelectedSessionIds] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [activeMonth, setActiveMonth] = useState(new Date());
  const [form, setForm] = useState(emptyBooking);
  const [submitting, setSubmitting] = useState(false);
  const derived = getBookingDerivedState(sessions, bookingType, activeMonth, selectedDate, selectedSessionIds);
  const handleChange = createBookingChangeHandler(setForm);
  const selectDate = createBookingDateSelector(setSelectedDate);
  const toggleSession = createSessionToggleHandler(setSelectedSessionIds);
  const submitRequest = createBookingSubmitHandler({ derived, form, refreshAll, setForm, setSelectedSessionIds, setSubmitting });

  return {
    activeMonth,
    bookingType,
    effectiveSelectedDate: derived.effectiveSelectedDate,
    form,
    handleChange,
    openSessions: derived.openSessions,
    selectDate,
    selectedDaySessions: derived.selectedDaySessions,
    selectedSessions: derived.selectedSessions,
    setActiveMonth,
    setBookingType,
    toggleSession,
    submitRequest,
    submitting,
  };
}

function createBookingChangeHandler(setForm) {
  return (event) => {
    const { name, value, type, checked } = event.target;
    setForm((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
  };
}

function createBookingDateSelector(setSelectedDate) {
  return (date) => {
    setSelectedDate(date);
  };
}

function createSessionToggleHandler(setSelectedSessionIds) {
  return (slot) => {
    setSelectedSessionIds((current) => (
      current.includes(slot.id) ? current.filter((id) => id !== slot.id) : [...current, slot.id]
    ));
  };
}

function getBookingErrorMessage(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail.length > 0) return detail.map((item) => item.msg || item.message || "Booking detail error").join(". ");
  if (error?.message) return error.message;
  return "Booking could not be confirmed";
}

async function submitBookingRequest(form, selectedSessions) {
  const sessionIds = selectedSessions.map((session) => session.id);
  try {
    return await axios.post(`${API}/bookings/bulk`, { ...form, session_ids: sessionIds });
  } catch (error) {
    const bulkEndpointMissing = error?.response?.status === 404 || error?.response?.status === 405;
    if (!bulkEndpointMissing || sessionIds.length !== 1) throw error;
    return axios.post(`${API}/bookings`, { ...form, session_id: sessionIds[0] });
  }
}

function getConfirmedBookingMessage(selectedSessions) {
  const sessionLabel = `${selectedSessions.length} session${selectedSessions.length > 1 ? "s" : ""} confirmed`;
  const sessionDetails = selectedSessions
    .map((session) => `${formatDate(session.date)} ${formatTime(session.start_time)}-${formatTime(session.end_time)}`)
    .join(" · ");
  return `${sessionLabel}: ${sessionDetails}. Check your email.`;
}

function createBookingSubmitHandler({ derived, form, refreshAll, setForm, setSelectedSessionIds, setSubmitting }) {
  return async (event) => {
    event.preventDefault();
    if (derived.selectedSessions.length === 0) {
      toast.error("Choose at least one open session first");
      return;
    }
    setSubmitting(true);
    try {
      if (derived.selectedSessions.some((session) => session.fallback)) throw new Error("Availability is still loading. Please wait a moment and try again.");
      await submitBookingRequest(form, derived.selectedSessions);
      toast.success(getConfirmedBookingMessage(derived.selectedSessions));
      setForm(emptyBooking);
      setSelectedSessionIds([]);
      await refreshAll();
    } catch (error) {
      toast.error(getBookingErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };
}

function getBookingDerivedState(sessions, bookingType, activeMonth, selectedDate, selectedSessionIds) {
  const serverOpenSessions = dedupeSessions(sessions.filter((session) => session.status === "open" && (session.session_type || "private") === bookingType));
  const fallbackMonthSessions = buildCalendarDays(activeMonth).flatMap((day) => getClientAvailabilityForDate(isoDate(day), bookingType));
  const openSessions = serverOpenSessions.length ? serverOpenSessions : fallbackMonthSessions;
  const firstAvailableDate = openSessions[0]?.date || null;
  const effectiveSelectedDate = selectedDate && openSessions.some((session) => session.date === selectedDate) ? selectedDate : firstAvailableDate;
  const selectedDaySessions = effectiveSelectedDate ? openSessions.filter((session) => session.date === effectiveSelectedDate) : [];
  const selectedSessionIdSet = new Set(selectedSessionIds);
  const selectedSessions = openSessions.filter((session) => selectedSessionIdSet.has(session.id));
  return { effectiveSelectedDate, openSessions, selectedDaySessions, selectedSessions };
}

function BookingHeroSection({ trainer }) {
  return (
    <section className="booking-hero" data-testid="booking-hero-section">
      <div className="relative mx-auto grid max-w-7xl content-center gap-10 px-5 py-14 lg:grid-cols-[1.05fr_0.95fr] lg:px-8">
        <div className="max-w-3xl animate-in" data-testid="hero-copy">
          <span className="eyebrow" data-testid="hero-eyebrow">Request-only private coaching</span>
          <h1 className="mt-4 font-display text-5xl tracking-normal text-white sm:text-6xl" data-testid="hero-title">Book your sessions with me.</h1>
          <p className="mt-5 max-w-2xl text-base leading-8 text-[#D1D5DB] md:text-lg" data-testid="hero-description">Choose Private Session or Group Class, click a calendar day, then select one or more available times. Bookings are confirmed instantly by email.</p>
          <div className="mt-8 grid max-w-2xl grid-cols-2 gap-3 sm:grid-cols-4" data-testid="hero-stats-row">
            <StatCard icon={Clock} label="Session" value={`${trainer?.session_length_minutes || 60}m`} testId="hero-session-length" />
            <StatCard icon={ShieldCheck} label="Format" value="1:1" testId="hero-format-type" />
            <StatCard icon={ShieldCheck} label="Booking" value="Instant" testId="hero-session-request" />
            <StatCard icon={CalendarDays} label="Confirm" value="Email" testId="hero-confirm-mode" />
          </div>
        </div>
        <aside className="trainer-panel animate-in delay-2" data-testid="trainer-profile-panel">
          <div className={`brand-statement-panel photo-panel ${TRAINER_VISUAL_MIN_HEIGHT_CLASS}`} data-testid="trainer-brand-visual-panel">
            <img src={APP_ASSETS.book} alt="Private boxing session in the ring" data-testid="trainer-brand-photo" />
            <div className="photo-panel-overlay" data-testid="trainer-brand-photo-overlay" />
            <span className="eyebrow" data-testid="trainer-brand-eyebrow">PRIVATE BOXING SESSION</span>
            <p className="font-display text-5xl tracking-normal" data-testid="trainer-brand-copy">Choose. Confirm. Train.</p>
          </div>
          <div className="border-x border-b border-[#262626] bg-[#141414] p-5" data-testid="trainer-profile-copy"><p className="eyebrow" data-testid="trainer-title">{trainer?.title}</p></div>
        </aside>
      </div>
    </section>
  );
}

function BookingSelectionPanel({ booking, loading }) {
  return (
    <div data-testid="session-selection-panel">
      <div className="section-heading" data-testid="session-selection-heading">
        <span className="eyebrow" data-testid="available-slots-eyebrow">Available openings</span>
        <h2 className="font-display text-4xl tracking-normal" data-testid="available-slots-title">Pick days and times</h2>
      </div>
      <BookingTypeTabs bookingType={booking.bookingType} setBookingType={booking.setBookingType} />
      <BookingAvailability booking={booking} loading={loading} />
      <div className="mt-5 border border-[#262626] bg-[#141414] p-5" data-testid="booking-review-note">
        <p className="text-sm leading-7 text-[#D1D5DB]" data-testid="booking-review-copy">Monday to Saturday: 7:00 AM to 8:00 PM. Each slot is 1 hour. Sundays are closed.</p>
      </div>
    </div>
  );
}

function BookingAvailability({ booking, loading }) {
  if (loading) return <div className="empty-state" data-testid="sessions-loading-state">Loading training slots...</div>;
  if (booking.openSessions.length === 0) return <div className="empty-state" data-testid="no-open-sessions-state">{getEmptySessionMessage(booking.bookingType)}</div>;
  return (
    <>
      <BookingCalendar sessions={booking.openSessions} selectedDate={booking.effectiveSelectedDate} onSelectDate={booking.selectDate} activeMonth={booking.activeMonth} setActiveMonth={booking.setActiveMonth} sessionType={booking.bookingType} />
      <div className="time-options-panel" data-testid="session-slot-grid">
        <p className="eyebrow" data-testid="available-times-label">Available times</p>
        {booking.selectedDaySessions.map((session) => (
          <SessionCard key={session.id} session={session} selected={booking.selectedSessions.some((selected) => selected.id === session.id)} onSelect={booking.toggleSession} />
        ))}
      </div>
    </>
  );
}

function SelectedSessionsSummary({ selectedSessions }) {
  if (selectedSessions.length === 0) {
    return (
      <section className="selected-summary" data-testid="selected-session-summary">
        <p data-testid="selected-session-summary-empty">Select one or more sessions</p>
      </section>
    );
  }
  return (
    <section className="selected-summary" data-testid="selected-session-summary">
      <div className="mb-3 flex items-center justify-between gap-3" data-testid="selected-session-summary-header">
        <span className="eyebrow" data-testid="selected-session-summary-title">Selected sessions</span>
        <strong className="font-display text-2xl font-normal tracking-normal" data-testid="selected-session-summary-count">{selectedSessions.length}</strong>
      </div>
      <div className="grid gap-2" data-testid="selected-session-summary-list">
        {selectedSessions.map((session) => (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[#262626] pt-2" data-testid={`selected-session-summary-item-${session.id}`} key={session.id}>
            <span data-testid={`selected-session-summary-date-${session.id}`}>{formatDate(session.date)}</span>
            <span className="text-[#81D8D0]" data-testid={`selected-session-summary-time-${session.id}`}>{formatTime(session.start_time)}–{formatTime(session.end_time)}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function BookingRequestForm({ booking }) {
  return (
    <form className="booking-form" data-testid="booking-request-form" onSubmit={booking.submitRequest}>
      <div className="section-heading" data-testid="booking-form-heading">
        <span className="eyebrow" data-testid="booking-form-eyebrow">Client details</span>
        <h2 className="font-display text-4xl tracking-normal" data-testid="booking-form-title">Confirm booking</h2>
      </div>
      <BookingFields bookingType={booking.bookingType} form={booking.form} handleChange={booking.handleChange} />
      <AgreementSection form={booking.form} handleChange={booking.handleChange} />
      <SelectedSessionsSummary selectedSessions={booking.selectedSessions} />
      <button className="primary-button" data-testid="submit-booking-request-button" disabled={booking.submitting || booking.selectedSessions.length === 0} type="submit">{booking.submitting ? "Confirming..." : "Confirm Booking"}</button>
    </form>
  );
}

function BookingPage({ data }) {
  const { trainer, loading } = data;
  const booking = useBookingFlow(data);

  return (
    <main data-testid="booking-page">
      <BookingHeroSection trainer={trainer} />

      <section className="mx-auto grid max-w-7xl gap-8 px-5 py-14 lg:grid-cols-[0.9fr_1.1fr] lg:px-8" data-testid="booking-workspace">
        <BookingSelectionPanel booking={booking} loading={loading} />
        <BookingRequestForm booking={booking} />
      </section>
    </main>
  );
}

function StatusPage({ data }) {
  const { sessions } = data;
  const [email, setEmail] = useState("");
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);

  const sessionMap = buildSessionMap(sessions);

  const searchStatus = async (event) => {
    event.preventDefault();
    try {
      const response = await axios.get(`${API}/bookings`, { params: { email } });
      setResults(response.data);
      setSearched(true);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not find requests");
    }
  };

  return (
    <main className="mx-auto max-w-5xl px-5 py-14 lg:px-8" data-testid="status-page">
      <div className="section-heading" data-testid="status-page-heading">
        <span className="eyebrow" data-testid="status-eyebrow">Request lookup</span>
        <h1 className="font-display text-5xl tracking-normal sm:text-6xl" data-testid="status-title">Check booking status</h1>
      </div>
      <form className="lookup-form" data-testid="status-lookup-form" onSubmit={searchStatus}>
        <label className="flex-1" data-testid="status-email-label">Email used for request
          <input data-testid="status-email-input" type="email" value={email} onChange={(event) => setEmail(event.target.value)} required placeholder="you@email.com" />
        </label>
        <button className="primary-button" data-testid="status-search-button" type="submit">Search</button>
      </form>
      <div className="mt-8 grid gap-3" data-testid="status-results-list">
        {results.map((booking) => {
          const session = sessionMap[booking.session_id];
          return (
            <article className="request-row" data-testid={`status-result-${booking.id}`} key={booking.id}>
              <div>
                <p className="font-display text-2xl tracking-normal" data-testid={`status-result-session-${booking.id}`}>{session ? `${formatDate(session.date)} · ${formatTime(session.start_time)}` : "Session details loading"}</p>
                <p className="text-sm text-[#9CA3AF]" data-testid={`status-result-client-${booking.id}`}>{booking.client_name}</p>
              </div>
              <span className={statusClass(booking.status)} data-testid={`status-result-badge-${booking.id}`}>{booking.status}</span>
            </article>
          );
        })}
        {searched && results.length === 0 && <div className="empty-state" data-testid="status-empty-state">No requests found for this email.</div>}
      </div>
    </main>
  );
}

function ContactPage() {
  return (
    <main className="mx-auto max-w-7xl px-5 py-14 lg:px-8" data-testid="contact-page">
      <section className="grid gap-8 lg:grid-cols-[1fr_0.9fr]" data-testid="contact-layout">
        <div data-testid="contact-copy-block">
          <span className="eyebrow" data-testid="contact-eyebrow">Contact</span>
          <h1 className="mt-4 font-display text-5xl tracking-normal sm:text-6xl" data-testid="contact-title">Start with a focused booking.</h1>
          <p className="mt-5 max-w-2xl text-base leading-8 text-[#D4D4D8]" data-testid="contact-description">For training questions, session fit, or availability, book your session online and you’ll receive confirmation by email.</p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2" data-testid="contact-info-grid">
            <article className="premium-card" data-testid="contact-email-card"><Mail className="text-[#81D8D0]" /><h2 className="mt-4 font-display text-3xl tracking-normal" data-testid="contact-email-title">Email</h2><p className="text-sm text-[#A1A1AA]" data-testid="contact-email-value">staysharpwme33@gmail.com</p></article>
            <article className="premium-card" data-testid="contact-social-card"><Instagram className="text-[#81D8D0]" /><h2 className="mt-4 font-display text-3xl tracking-normal" data-testid="contact-social-title">Instagram</h2><a className="text-sm text-[#A1A1AA] transition-colors hover:text-[#81D8D0]" data-testid="contact-instagram-link" href="https://www.instagram.com/matteopapa33/" target="_blank" rel="noopener noreferrer">@matteopapa33</a></article>
          </div>
          <NavLink to="/book" className="primary-button mt-8" data-testid="contact-book-button">Book a session <ArrowRight size={ICON_SIZE_SM} className="ml-2" /></NavLink>
        </div>
        <div className="brand-statement-panel photo-panel contact-photo-panel" data-testid="contact-brand-panel">
          <img src={APP_ASSETS.contact} alt="Boxer in the ring" data-testid="contact-brand-photo" />
          <div className="photo-panel-overlay" data-testid="contact-brand-photo-overlay" />
          <span className="eyebrow" data-testid="contact-brand-eyebrow">Start sharp</span>
          <p className="font-display text-5xl tracking-normal" data-testid="contact-brand-copy">Book the time. Bring the focus.</p>
        </div>
      </section>
    </main>
  );
}

function DashboardMetrics({ stats }) {
  return (
    <section className="dashboard-metrics" data-testid="dashboard-metrics-grid">
      <StatCard icon={Clock} label="Pending" value={stats?.pending_requests ?? 0} testId="metric-pending-requests" />
      <StatCard icon={Check} label="Approved" value={stats?.approved_sessions ?? 0} testId="metric-approved-sessions" />
      <StatCard icon={CalendarDays} label="Open" value={stats?.open_slots ?? 0} testId="metric-open-slots" />
      <StatCard icon={Users} label="Clients" value={stats?.total_clients ?? 0} testId="metric-total-clients" />
    </section>
  );
}

function PendingRequestsPanel({ pendingBookings, sessionMap, updateBooking }) {
  return (
    <div className="dashboard-panel dashboard-panel-large" data-testid="pending-requests-panel">
      <div className="panel-header" data-testid="pending-requests-heading">
        <h2 className="font-display text-3xl tracking-normal" data-testid="pending-requests-title">Pending requests</h2>
        <span className="status status-pending" data-testid="pending-requests-count">{pendingBookings.length} waiting</span>
      </div>
      <div className="grid gap-3" data-testid="pending-requests-list">
        {pendingBookings.map((booking) => {
          const session = sessionMap[booking.session_id];
          return (
            <article className="request-row" data-testid={`pending-request-${booking.id}`} key={booking.id}>
              <div>
                <p className="font-semibold" data-testid={`pending-client-name-${booking.id}`}>{booking.client_name}</p>
                <p className="text-sm text-[#9CA3AF]" data-testid={`pending-session-time-${booking.id}`}>{session ? `${formatDate(session.date)} · ${formatTime(session.start_time)}` : "Slot held"}</p>
                {booking.notes && <p className="mt-2 text-sm text-[#D1D5DB]" data-testid={`pending-client-notes-${booking.id}`}>{booking.notes}</p>}
              </div>
              <div className="flex gap-2" data-testid={`pending-actions-${booking.id}`}>
                <button className="icon-button approve" data-testid={`approve-booking-${booking.id}`} onClick={() => updateBooking(booking.id, "approved")} aria-label="Approve booking"><Check size={ICON_SIZE_SM} /></button>
                <button className="icon-button reject" data-testid={`reject-booking-${booking.id}`} onClick={() => updateBooking(booking.id, "rejected")} aria-label="Reject booking"><X size={ICON_SIZE_SM} /></button>
              </div>
            </article>
          );
        })}
        {pendingBookings.length === 0 && <div className="empty-state" data-testid="no-pending-requests-state">No pending requests.</div>}
      </div>
    </div>
  );
}

function AddSessionForm({ slotForm, setSlotForm, createSlot }) {
  return (
    <form className="dashboard-panel" data-testid="add-session-form" onSubmit={createSlot}>
      <div className="panel-header" data-testid="add-session-heading">
        <h2 className="font-display text-3xl tracking-normal" data-testid="add-session-title">Add slot</h2>
        <Plus size={18} className="text-[#81D8D0]" aria-hidden="true" />
      </div>
      <div className="compact-form" data-testid="add-session-fields">
        <label data-testid="slot-date-label">Date<select data-testid="slot-date-select" value={slotForm.date} onChange={(event) => setSlotForm({ ...slotForm, date: event.target.value })} required>{dateOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
        <label data-testid="slot-start-label">Start<select data-testid="slot-start-select" value={slotForm.start_time} onChange={(event) => setSlotForm({ ...slotForm, start_time: event.target.value })} required>{timeOptions.map((time) => <option key={time} value={time}>{time}</option>)}</select></label>
        <label data-testid="slot-end-label">End<select data-testid="slot-end-select" value={slotForm.end_time} onChange={(event) => setSlotForm({ ...slotForm, end_time: event.target.value })} required>{timeOptions.map((time) => <option key={time} value={time}>{time}</option>)}</select></label>
        <label data-testid="slot-focus-label">Focus<select data-testid="slot-focus-select" value={slotForm.focus} onChange={(event) => setSlotForm({ ...slotForm, focus: event.target.value })}>{focusOptions.map((option) => <option key={option}>{option}</option>)}</select></label>
      </div>
      <button className="primary-button w-full" data-testid="add-session-submit-button" type="submit">Add Private Slot</button>
    </form>
  );
}

function SchedulePanel({ sessions }) {
  return (
    <div className="dashboard-panel" data-testid="schedule-panel">
      <div className="panel-header" data-testid="schedule-heading">
        <h2 className="font-display text-3xl tracking-normal" data-testid="schedule-title">Schedule</h2>
        <CalendarDays size={18} className="text-[#81D8D0]" aria-hidden="true" />
      </div>
      <div className="stack-list" data-testid="schedule-list">
        {sessions.slice(0, 8).map((session) => (
          <div className="mini-row" data-testid={`schedule-row-${session.id}`} key={session.id}>
            <span data-testid={`schedule-date-${session.id}`}>{formatDate(session.date)}</span>
            <strong data-testid={`schedule-time-${session.id}`}>{formatTime(session.start_time)}</strong>
            <span className={statusClass(session.status)} data-testid={`schedule-status-${session.id}`}>{session.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ClientListPanel({ clients }) {
  return (
    <div className="dashboard-panel dashboard-panel-large" data-testid="client-list-panel">
      <div className="panel-header" data-testid="client-list-heading">
        <h2 className="font-display text-3xl tracking-normal" data-testid="client-list-title">Client list</h2>
        <Users size={18} className="text-[#81D8D0]" aria-hidden="true" />
      </div>
      <div className="stack-list" data-testid="client-list">
        {clients.map((client) => (
          <article className="client-row" data-testid={`client-row-${client.id}`} key={client.id}>
            <div className="grid h-10 w-10 place-items-center border border-[#262626]" data-testid={`client-avatar-${client.id}`}><UserRound size={ICON_SIZE_SM} /></div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold" data-testid={`client-name-${client.id}`}>{client.name}</p>
              <p className="truncate text-sm text-[#9CA3AF]" data-testid={`client-contact-${client.id}`}><Mail size={INLINE_ICON_SIZE} className="inline" /> {client.email} · <Phone size={INLINE_ICON_SIZE} className="inline" /> {client.phone}</p>
            </div>
            <span className="status status-approved" data-testid={`client-approved-count-${client.id}`}>{client.approved_sessions} approved</span>
          </article>
        ))}
        {clients.length === 0 && <div className="empty-state" data-testid="no-clients-state">Client list will build as requests arrive.</div>}
      </div>
    </div>
  );
}

function NotificationsPanel({ notifications }) {
  return (
    <div className="dashboard-panel" data-testid="whatsapp-notification-panel">
      <div className="panel-header" data-testid="whatsapp-notification-heading">
        <h2 className="font-display text-3xl tracking-normal" data-testid="whatsapp-notification-title">WhatsApp alerts</h2>
        <Phone size={18} className="text-[#81D8D0]" aria-hidden="true" />
      </div>
      <p className="mb-4 text-sm leading-7 text-[#A1A1AA]" data-testid="whatsapp-mock-note">MOCKED structure only. My number can be connected later.</p>
      <div className="stack-list" data-testid="whatsapp-notification-list">
        {notifications.slice(0, 5).map((notification) => (
          <article className="mini-row" data-testid={`whatsapp-notification-${notification.id}`} key={notification.id}>
            <span data-testid={`whatsapp-notification-message-${notification.id}`}>{notification.message}</span>
            <span className="status status-held" data-testid={`whatsapp-notification-status-${notification.id}`}>{notification.status}</span>
          </article>
        ))}
        {notifications.length === 0 && <div className="empty-state" data-testid="no-whatsapp-notifications-state">New booking requests will appear here.</div>}
      </div>
    </div>
  );
}

function DashboardPage({ data }) {
  const dashboard = useDashboardFlow(data);

  return (
    <main className="mx-auto max-w-7xl px-5 py-10 lg:px-8" data-testid="dashboard-page">
      <DashboardHeader />
      <DashboardMetrics stats={data.stats} />
      <section className="dashboard-grid" data-testid="dashboard-workspace-grid">
        <PendingRequestsPanel pendingBookings={dashboard.pendingBookings} sessionMap={dashboard.sessionMap} updateBooking={dashboard.updateBooking} />
        <AddSessionForm slotForm={dashboard.slotForm} setSlotForm={dashboard.setSlotForm} createSlot={dashboard.createSlot} />
        <SchedulePanel sessions={data.sessions} />
        <ClientListPanel clients={data.clients} />
        <NotificationsPanel notifications={dashboard.notifications} />
      </section>
    </main>
  );
}

function DashboardHeader() {
  return (
    <div className="section-heading" data-testid="dashboard-heading">
      <span className="eyebrow" data-testid="dashboard-eyebrow">Trainer control room</span>
      <h1 className="font-display text-5xl tracking-normal sm:text-6xl" data-testid="dashboard-title">Bookings, schedule, clients</h1>
    </div>
  );
}

function useDashboardFlow(data) {
  const { sessions, bookings, refreshAll } = data;
  const notificationsQuery = useSWR(`${API}/notifications`, fetcher);
  const [slotForm, setSlotForm] = useState(emptySlot);
  const sessionMap = buildSessionMap(sessions);

  const updateBooking = async (bookingId, status) => {
    try {
      await axios.patch(`${API}/bookings/${bookingId}/status`, { status });
      toast.success(`Request ${status}`);
      await refreshAll();
      await notificationsQuery.mutate();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Request could not be updated");
    }
  };

  const createSlot = async (event) => {
    event.preventDefault();
    try {
      await axios.post(`${API}/sessions`, slotForm);
      toast.success("Private slot added");
      setSlotForm(emptySlot);
      await refreshAll();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Slot could not be added");
    }
  };

  const pendingBookings = bookings.filter((booking) => booking.status === "pending");
  return {
    createSlot,
    notifications: notificationsQuery.data || [],
    pendingBookings,
    sessionMap,
    setSlotForm,
    slotForm,
    updateBooking,
  };
}

function App() {
  const data = useStudioData();
  return (
    <BrowserRouter>
      <Shell>
        <Routes>
          <Route path="/" element={<HomePage data={data} />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/book" element={<BookingPage data={data} />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/trainer-dashboard" element={<DashboardPage data={data} />} />
          <Route path="/dashboard" element={<DashboardPage data={data} />} />
        </Routes>
      </Shell>
    </BrowserRouter>
  );
}

export default App;
